import axios from 'axios';

// =======================================================================================
// CLOUDFLARE WORKERS CONFIGURATION
// =======================================================================================
// This project uses ONLY Cloudflare Workers - no traditional backend servers
// All API endpoints are served by edge-located Workers with D1 database access

// Cloudflare D1 Worker URL for analytics, hardware, and sync (edge-located data)
export const D1_WORKER_URL = import.meta.env.VITE_ANALYTICS_WORKER_URL || 'https://evesix-analytics-worker.reshigan-085.workers.dev';

// Cloudflare Auth Worker URL for authentication and user management
const AUTH_WORKER_URL = import.meta.env.VITE_AUTH_WORKER_URL || 'https://evesix-auth-worker.reshigan-085.workers.dev';

// Legacy: VITE_API_BASE_URL is no longer used (kept for backward compatibility with tunnels)
const rawApiUrl = import.meta.env.VITE_API_BASE_URL || D1_WORKER_URL;
let API_BASE_URL = rawApiUrl;
let TUNNEL_AUTH = null;

// Extract basic auth from URL if present (for development tunnels only)
try {
  const urlObj = new URL(rawApiUrl);
  if (urlObj.username && urlObj.password) {
    TUNNEL_AUTH = btoa(`${urlObj.username}:${urlObj.password}`);
    API_BASE_URL = `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`;
  }
} catch (e) {
  console.warn('Could not parse API URL:', e);
}

// All endpoints use Cloudflare Workers only
const ENDPOINTS = {
  auth: AUTH_WORKER_URL,
  hardware: D1_WORKER_URL,
  sync: D1_WORKER_URL,
  fieldService: AUTH_WORKER_URL,
  kpi: D1_WORKER_URL,
  userManagement: AUTH_WORKER_URL,
};

// Axios instance with auth interceptor (for auth worker calls that need Bearer token)
const apiClient = axios.create({
  timeout: 90000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth worker client (includes Bearer token, no withCredentials to avoid CORS issues)
const authClient = axios.create({
  baseURL: AUTH_WORKER_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add basic auth header to all requests if tunnel auth is present
if (TUNNEL_AUTH) {
  apiClient.defaults.headers.common['Authorization'] = `Basic ${TUNNEL_AUTH}`;
}

// Request interceptor to add auth token to apiClient
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('portal_auth_token');
    if (token) {
      if (TUNNEL_AUTH) {
        config.headers['X-Portal-Token'] = token;
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add Bearer token to authClient requests
authClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('portal_auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('portal_auth_token');
      localStorage.removeItem('user_info');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ===========================================
// Authentication API
// ===========================================
export const authAPI = {
  login: async (email, password) => {
    const response = await apiClient.post(`${ENDPOINTS.auth}/login`, {
      email,
      password,
    });
    if (response.data.token) {
      localStorage.setItem('portal_auth_token', response.data.token);
      localStorage.setItem('user_info', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: async () => {
    try {
      await apiClient.post(`${ENDPOINTS.auth}/logout`);
    } finally {
      localStorage.removeItem('portal_auth_token');
      localStorage.removeItem('user_info');
    }
  },

  getCurrentUser: () => {
    const userInfo = localStorage.getItem('user_info');
    return userInfo ? JSON.parse(userInfo) : null;
  },

  verifyToken: async () => {
    return apiClient.get(`${ENDPOINTS.auth}/verify`);
  },

  getAllowedHospitals: async () => {
    return authClient.get('/auth/allowed-hospitals');
  },
};

// ===========================================
// KPI & Analytics API
// ===========================================
export const kpiAPI = {
  // Dashboard-level KPIs
  getDashboardKPIs: async (timeRange = 'today') => {
    return d1Client.get('/kpi/dashboard', {
      params: { time_range: timeRange },
    });
  },

  // Hospital-specific KPIs
  getHospitalKPIs: async (hospitalId, timeRange = 'today') => {
    return d1Client.get(`/kpi/hospital/${hospitalId}`, {
      params: { time_range: timeRange },
    });
  },

  // Robot-specific KPIs
  getRobotKPIs: async (robotId, timeRange = 'today') => {
    return d1Client.get(`/kpi/robot/${robotId}`, {
      params: { time_range: timeRange },
    });
  },

  // Trends analysis
  getTrends: async (metricType, timeRange, granularity = 'day') => {
    return d1Client.get(`/kpi/trends/${metricType}`, {
      params: { time_range: timeRange, granularity },
    });
  },

  // Comparative analysis
  compareHospitals: async (hospitalIds, timeRange = 'month') => {
    return d1Client.post('/kpi/compare/hospitals', {
      hospital_ids: hospitalIds,
      time_range: timeRange,
    });
  },

  compareRobots: async (robotIds, timeRange = 'month') => {
    return d1Client.post('/kpi/compare/robots', {
      robot_ids: robotIds,
      time_range: timeRange,
    });
  },

  // Performance benchmarks
  getBenchmarks: async () => {
    return d1Client.get('/kpi/benchmarks');
  },
};

// ===========================================
// Field Service API
// ===========================================
export const fieldServiceAPI = {
  // Tickets
  getTickets: async (filters = {}) => {
    return authClient.get('/tickets', { params: filters });
  },

  getTicket: async (ticketId) => {
    return authClient.get(`/tickets/${ticketId}`);
  },

  createTicket: async (ticketData) => {
    return authClient.post('/tickets', ticketData);
  },

  updateTicket: async (ticketId, updates) => {
    return authClient.put(`/tickets/${ticketId}`, updates);
  },

  assignTicket: async (ticketId, technicianId) => {
    return authClient.post(`/tickets/${ticketId}/assign`, { technician_id: technicianId });
  },

  // Diagnostics
  getDiagnosticHistory: async (robotId, limit = 50) => {
    return authClient.get(`/diagnostics/robot/${robotId}`, { params: { limit } });
  },

  // Maintenance
  getMaintenanceSchedule: async (robotId) => {
    return authClient.get(`/maintenance/robot/${robotId}`);
  },

  scheduleMaintenance: async (maintenanceData) => {
    return authClient.post('/maintenance/schedule', maintenanceData);
  },

  completeMaintenanceTask: async (taskId, notes) => {
    return authClient.post(`/maintenance/${taskId}/complete`, { completion_notes: notes });
  },
};

// ===========================================
// User Management API
// ===========================================
export const userManagementAPI = {
  // Users CRUD
  getUsers: async (filters = {}) => {
    return authClient.get('/users', { params: filters });
  },

  getUser: async (userId) => {
    return authClient.get(`/users/${userId}`);
  },

  createUser: async (userData) => {
    return authClient.post('/users', userData);
  },

  updateUser: async (userId, updates) => {
    return authClient.put(`/users/${userId}`, updates);
  },

  deleteUser: async (userId) => {
    return authClient.delete(`/users/${userId}`);
  },

  // Roles and Permissions
  getRoles: async () => {
    return authClient.get('/roles');
  },

  getUserPermissions: async (userId) => {
    return authClient.get(`/users/${userId}/permissions`);
  },

  updateUserPermissions: async (userId, permissions) => {
    return authClient.put(`/users/${userId}/permissions`, { permissions });
  },

  // Activity logs
  getUserActivityLogs: async (userId, limit = 100) => {
    return authClient.get(`/users/${userId}/activity`, { params: { limit } });
  },
};

// ===========================================
// Hardware & Robot API
// ===========================================
export const hardwareAPI = {
  getRobots: async (hospitalId = null) => {
    const params = hospitalId ? { hospital_id: hospitalId } : {};
    return d1Client.get('/hardware/robots', { params });
  },

  getRobotStatus: async (robotId) => {
    return d1Client.get(`/hardware/robots/${robotId}/status`);
  },

  getRobotDiagnostics: async (robotId) => {
    return d1Client.get(`/hardware/robots/${robotId}/diagnostics`);
  },

  getHospitals: async () => {
    return d1Client.get('/hardware/hospitals');
  },
};

// ===========================================
// Sync & Session Data API
// ===========================================
export const syncAPI = {
  getSessions: async (filters = {}) => {
    return d1Client.get('/sync/sessions', { params: filters });
  },

  getSession: async (sessionId) => {
    return d1Client.get(`/sync/sessions/${encodeURIComponent(sessionId)}`);
  },

  getSyncStatus: async (robotId) => {
    return d1Client.get(`/sync/status/robot/${robotId}`);
  },

  getQueueStatus: async () => {
    return d1Client.get('/sync/queue/status');
  },
};

// ===========================================
// Analytics API (uses D1 Worker for fast edge queries)
// ===========================================

// D1 Worker client for fast analytics (requires JWT auth with hospital group filtering)
const d1Client = axios.create({
  baseURL: D1_WORKER_URL,
  timeout: 15000, // Increased to 15 seconds to handle slow D1 queries
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add Bearer token to d1Client requests (required for hospital group filtering)
d1Client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('portal_auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Helper function to try D1 Worker first, then fallback to backend
// D1 Worker now supports hospital_id, robot_id, days, start_date, and end_date filters
const tryD1First = async (d1Path, backendPath, params = {}) => {
  try {
    const response = await d1Client.get(d1Path, { params });
    return { data: response.data, source: 'd1' };
  } catch (error) {
    console.warn(`D1 Worker unavailable for ${d1Path}, falling back to backend:`, error.message);
    return apiClient.get(`${ENDPOINTS.kpi}${backendPath}`, { params });
  }
};

// Helper function to use D1 sessions/aggregate for accurate filtering
// This queries full session data and aggregates on-the-fly for accurate robot/date filtering
const tryD1SessionsAggregate = async (groupBy, backendPath, params = {}) => {
  try {
    const d1Params = { ...params, group_by: groupBy };
    const response = await d1Client.get('/analytics/sessions/aggregate', { params: d1Params });
    return { data: response.data, source: 'd1_sessions' };
  } catch (error) {
    console.warn(`D1 sessions/aggregate unavailable for ${groupBy}, falling back to backend:`, error.message);
    return apiClient.get(`${ENDPOINTS.kpi}${backendPath}`, { params });
  }
};

export const analyticsAPI = {
  // Fast KPIs from D1 Worker /analytics/kpis endpoint
  getPerformanceKPIs: async (hospitalId = null, days = 30, robotId = null, startDate = null, endDate = null) => {
    const params = {};
    if (days != null) params.days = days; // Only send days if not null
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    // Use D1 Worker for fast edge-located analytics
    return tryD1First('/analytics/kpis', '/analytics/performance-kpis', params);
  },

  getKillswitchKPIs: async (days = 30, hospitalId = null, robotId = null, startDate = null, endDate = null) => {
    const params = { days };
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    // Try backend first for killswitch data
    try {
      return await apiClient.get(`${ENDPOINTS.kpi}/kpi/killswitch`, { params });
    } catch (error) {
      console.warn('Backend killswitch KPI failed:', error.message);
      // Return default empty state if both fail
      return { 
        data: {
          total_all_time: 0,
          period_stops: 0,
          period_days: days,
          emergency_stop_rate: 0,
          affected_robots: 0,
          total_sessions_period: 0,
          top_operators: [],
          top_robots: [],
          daily_trend: [],
          latest_events: []
        }
      };
    }
  },

  getTrends: async (hospitalId = null, days = 30, robotId = null, startDate = null, endDate = null) => {
    const params = {};
    if (days != null) params.days = days; // Only send days if not null
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    // Always use sessions/aggregate for accurate trends from full session data
    try {
      const response = await d1Client.get('/analytics/sessions/aggregate', { 
        params: { ...params, group_by: 'date' } 
      });
      // Transform to trends format
      const data = response.data.data || [];
      return { 
        data: {
          weekly: data.map(d => ({
            week: d.date,
            sessions: d.total_sessions,
            success_rate: d.success_rate,
            robots: d.active_robots || 0,
            operators: d.active_operators || 0
          }))
        }, 
        source: 'd1_sessions' 
      };
    } catch (error) {
      console.warn('D1 sessions/aggregate unavailable for trends, falling back to backend:', error.message);
      return apiClient.get(`${ENDPOINTS.kpi}/analytics/trends`, { params });
    }
  },

  getRobots: async (hospitalId = null, days = 30, robotId = null, startDate = null, endDate = null) => {
    const params = {};
    if (days != null) params.days = days;
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    // Use sessions/aggregate grouped by robot for accurate robot data
    try {
      const response = await d1Client.get('/analytics/sessions/aggregate', {
        params: { ...params, group_by: 'robot' }
      });
      const data = response.data.data || [];
      return {
        data: {
          robots: data.map(d => ({
            serial: d.serial,
            reference: d.reference,
            hospital: d.hospital_name || 'Unknown',
            total_sessions: d.total_sessions,
            successful: d.successful,
            failed: d.failed,
            success_rate: d.success_rate,
            avg_duration: d.avg_duration,
            total_hours: (d.total_sessions * (d.avg_duration || 0) / 60).toFixed(1)
          })),
          statistics: response.data.summary || {}
        },
        source: 'd1_sessions'
      };
    } catch (error) {
      console.warn('D1 sessions/aggregate unavailable for robots, falling back to D1 hardware:', error.message);
      return d1Client.get('/hardware/robots', { params });
    }
  },

  getInactiveRobots: async () => {
    return d1Client.get('/analytics/robots/inactive');
  },

  getOperators: async (hospitalId = null, days = 30, limit = 20, robotId = null, startDate = null, endDate = null) => {
    const params = { limit };
    if (days != null) params.days = days;
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    // Always use sessions/aggregate grouped by operator for accurate operator data
    try {
      const response = await d1Client.get('/analytics/sessions/aggregate', { 
        params: { ...params, group_by: 'operator' } 
      });
      // Transform to operators format
      const data = response.data.data || [];
      return { 
        data: {
          operators: data.slice(0, limit).map(d => ({
            name: d.name || 'Unknown',
            role: d.role || 'Unknown',
            total_sessions: d.total_sessions,
            successful: d.successful,
            failed: d.failed,
            success_rate: d.success_rate,
            avg_duration: d.avg_duration
          })),
          statistics: response.data.summary || {}
        }, 
        source: 'd1_sessions' 
      };
    } catch (error) {
      console.warn('D1 sessions/aggregate unavailable for operators, falling back to backend:', error.message);
      return apiClient.get(`${ENDPOINTS.kpi}/analytics/operators`, { params });
    }
  },

  getHourly: async (hospitalId = null, days = 30, robotId = null, startDate = null, endDate = null) => {
    const params = {};
    if (days != null) params.days = days;
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    // Always use sessions/aggregate grouped by hour for accurate hourly data
    try {
      const response = await d1Client.get('/analytics/sessions/aggregate', { 
        params: { ...params, group_by: 'hour' } 
      });
      // Transform to hourly format
      const data = response.data.data || [];
      return { 
        data: {
          hourly: data.map(d => ({
            hour: d.hour,
            sessions: d.total_sessions,
            success_rate: d.success_rate
          }))
        }, 
        source: 'd1_sessions' 
      };
    } catch (error) {
      console.warn('D1 sessions/aggregate unavailable for hourly, falling back to backend:', error.message);
      return apiClient.get(`${ENDPOINTS.kpi}/analytics/hourly`, { params });
    }
  },

  getErrors: async (hospitalId = null, days = 30, robotId = null, startDate = null, endDate = null) => {
    const params = {};
    if (days != null) params.days = days;
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return tryD1First('/analytics/errors', '/analytics/errors', params);
  },

  getHospitals: async (days = 30) => {
    const params = {};
    if (days != null) params.days = days;
    return tryD1First('/analytics/hospitals', '/analytics/hospitals', params);
  },

  getRobotPerformance: async (hospitalId = null, days = 30, robotId = null, startDate = null, endDate = null) => {
    const params = {};
    if (days != null && !startDate && !endDate) params.days = days;
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    // Use sessions/aggregate grouped by robot for accurate robot performance
    if (robotId || startDate || endDate) {
      return tryD1SessionsAggregate('robot', '/analytics/robot-performance', params);
    }
    return tryD1First('/analytics/robots', '/analytics/robot-performance', params);
  },

  getUvcAnalytics: async (hospitalId = null, days = 30, startDate = null, endDate = null) => {
    const params = {};
    if (days != null && !startDate && !endDate) params.days = days;
    if (hospitalId) params.hospital_id = hospitalId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    return tryD1First('/analytics/uvc', '/analytics/uvc', params);
  },

  getUvcSessionsDetail: async (hospitalId = null, days = 30, startDate = null, endDate = null, durationRange = null) => {
    const params = {};
    if (days != null && !startDate && !endDate) params.days = days;
    if (hospitalId) params.hospital_id = hospitalId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (durationRange) params.duration_range = durationRange;
    
    return tryD1First('/analytics/uvc/sessions', '/analytics/uvc/sessions', params);
  },

  getUvcBulbUsage: async (hospitalId = null, days = null, startDate = null, endDate = null) => {
    const params = {};
    if (days != null && !startDate && !endDate) params.days = days;
    if (hospitalId) params.hospital_id = hospitalId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    return tryD1First('/analytics/uvc/bulb-usage', '/analytics/uvc/bulb-usage', params);
  },

  getUvcBulbDetail: async (hospitalId = null, robotId = null, days = null, startDate = null, endDate = null) => {
    const params = {};
    if (days != null && !startDate && !endDate) params.days = days;
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    return d1Client.get('/analytics/uvc/bulb-detail', { params });
  },

  // UVC bulb reset records (per-robot hour resets)
  getUvcResets: async (serial = null, limit = 100) => {
    const params = { limit };
    if (serial) params.serial = serial;
    return d1Client.get('/sync/uvc-resets', { params });
  },

  // Record a UVC hours reset for one robot (super admin only — enforced server-side)
  createUvcReset: async (serial, resetDate, notes = null) => {
    return d1Client.post('/sync/uvc-resets', { serial, reset_date: resetDate, notes });
  },

  getOperatorDetails: async (hospitalId = null, days = 30, limit = 50, robotId = null, startDate = null, endDate = null) => {
    const params = { limit };
    if (days != null) params.days = days;
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    // Use sessions/aggregate grouped by operator for accurate operator details
    if (robotId || startDate || endDate) {
      return tryD1SessionsAggregate('operator', '/analytics/operator-details', params);
    }
    return tryD1First('/analytics/operators', '/analytics/operator-details', params);
  },

  getWardBed: async (hospitalId = null, days = 30, robotId = null, startDate = null, endDate = null) => {
    const params = {};
    if (days != null) params.days = days;
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    // Try D1 Worker first for sluice vs beds data
    try {
      const response = await d1Client.get('/analytics/wards', { params });
      return { data: response.data, source: 'd1_wards' };
    } catch (error) {
      console.warn('D1 Worker unavailable for /analytics/wards, falling back to backend:', error.message);
      
      // Fall back to backend and compute sluice vs beds from beds data
      const backendResponse = await apiClient.get(`${ENDPOINTS.kpi}/analytics/ward-bed`, { params });
      const data = backendResponse.data;
      
      // Compute sluice vs beds analysis from beds data
      if (data.beds && Array.isArray(data.beds)) {
        const sluiceBeds = data.beds.filter(b => 
          b.bed_name && b.bed_name.toLowerCase().includes('sluice')
        );
        const regularBeds = data.beds.filter(b => 
          !b.bed_name || !b.bed_name.toLowerCase().includes('sluice')
        );
        
        // Aggregate sluice data
        const sluiceStats = {
          space_type: 'Sluice',
          total_sessions: sluiceBeds.reduce((sum, b) => sum + (b.total_sessions || 0), 0),
          successful: sluiceBeds.reduce((sum, b) => sum + (b.successful || 0), 0),
          failed: sluiceBeds.reduce((sum, b) => sum + (b.failed || 0), 0),
          unique_spaces: sluiceBeds.length,
          robots_used: new Set(sluiceBeds.flatMap(b => b.robots_used || [])).size,
          operators: 0,
          avg_duration: 0,
          total_hours: 0
        };
        sluiceStats.success_rate = sluiceStats.total_sessions > 0 
          ? Math.round(sluiceStats.successful * 1000 / sluiceStats.total_sessions) / 10 
          : 0;
        
        // Aggregate bed data
        const bedStats = {
          space_type: 'Bed',
          total_sessions: regularBeds.reduce((sum, b) => sum + (b.total_sessions || 0), 0),
          successful: regularBeds.reduce((sum, b) => sum + (b.successful || 0), 0),
          failed: regularBeds.reduce((sum, b) => sum + (b.failed || 0), 0),
          unique_spaces: regularBeds.length,
          robots_used: new Set(regularBeds.flatMap(b => b.robots_used || [])).size,
          operators: 0,
          avg_duration: 0,
          total_hours: 0
        };
        bedStats.success_rate = bedStats.total_sessions > 0 
          ? Math.round(bedStats.successful * 1000 / bedStats.total_sessions) / 10 
          : 0;
        
        // Compute sluice by ward
        const sluiceByWardMap = {};
        sluiceBeds.forEach(b => {
          const key = `${b.ward || 'Unknown'}-${b.hospital || 'Unknown'}`;
          if (!sluiceByWardMap[key]) {
            sluiceByWardMap[key] = {
              ward_name: b.ward || 'Unknown',
              hospital_name: b.hospital || 'Unknown',
              sluice_sessions: 0,
              successful: 0,
              sluice_count: 0,
              avg_duration: 0
            };
          }
          sluiceByWardMap[key].sluice_sessions += b.total_sessions || 0;
          sluiceByWardMap[key].successful += b.successful || 0;
          sluiceByWardMap[key].sluice_count += 1;
        });
        
        const sluiceByWard = Object.values(sluiceByWardMap)
          .map(w => ({
            ...w,
            success_rate: w.sluice_sessions > 0 
              ? Math.round(w.successful * 1000 / w.sluice_sessions) / 10 
              : 0
          }))
          .sort((a, b) => b.sluice_sessions - a.sluice_sessions)
          .slice(0, 20);
        
        data.sluice_vs_beds = [bedStats, sluiceStats].filter(s => s.total_sessions > 0);
        data.sluice_by_ward = sluiceByWard;
      } else {
        data.sluice_vs_beds = [];
        data.sluice_by_ward = [];
      }
      
      return { data, source: 'backend_computed' };
    }
  },

  getInsights: async (hospitalId = null, days = 30, robotId = null, startDate = null, endDate = null) => {
    const params = {};
    if (days != null) params.days = days;
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    try {
      const response = await d1Client.get('/analytics/insights', { params });
      return { data: response.data, source: 'd1_insights' };
    } catch (error) {
      console.warn('D1 insights unavailable, falling back to backend:', error.message);
      return apiClient.get(`${ENDPOINTS.kpi}/analytics/insights`, { params });
    }
  },
  
  // Fleet drill-through: Get detailed session data for a specific robot
  getFleetSessions: async (hospitalId = null, robotId = null, operatorId = null, wardId = null, bedId = null, days = 30, startDate = null, endDate = null, allSerials = false) => {
    const params = {};
    if (days != null) params.days = days;
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (operatorId) params.operator_id = operatorId;
    if (wardId) params.ward_id = wardId;
    if (bedId) params.bed_id = bedId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (allSerials) params.all_serials = 'true';

    return apiClient.get(`${D1_WORKER_URL}/analytics/fleet-sessions`, { params });
  },
  
  // New: Get raw session data from D1 for drill-through
  getSessions: async (hospitalId = null, robotId = null, startDate = null, endDate = null, limit = 100, page = 1, errorCode = null) => {
    const params = {};
    if (limit) params.limit = limit;
    if (page) params.page = page;
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (errorCode) params.error_code = errorCode;
    
    console.log('getSessions called with:', { hospitalId, robotId, startDate, endDate, limit, page, errorCode });
    console.log('Built params:', params);
    
    try {
      const response = await d1Client.get('/analytics/sessions', { params });
      return { data: response.data, source: 'd1_sessions' };
    } catch (error) {
      console.warn('D1 sessions unavailable, falling back to backend:', error.message);
      return apiClient.get(`${ENDPOINTS.sync}/sessions`, { params });
    }
  },
  
  // New: Get aggregated session data from D1 with custom grouping
  getSessionsAggregate: async (groupBy = 'date', hospitalId = null, robotId = null, startDate = null, endDate = null) => {
    const params = { group_by: groupBy };
    if (hospitalId) params.hospital_id = hospitalId;
    if (robotId) params.robot_id = robotId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    try {
      const response = await d1Client.get('/analytics/sessions/aggregate', { params });
      return { data: response.data, source: 'd1_sessions' };
    } catch (error) {
      console.warn('D1 sessions/aggregate unavailable:', error.message);
      return { data: { data: [], summary: {} }, source: 'error' };
    }
  },

  // Internal: Get excluded test data (DEVBOT sessions, NXT operators, etc.)
  getTestData: async (params = {}) => {
    return d1Client.get('/analytics/test-data', { params });
  },

};

// ===========================================
// Report Scheduler API
// ===========================================
export const reportAPI = {
  // Generate PDF report on demand
  generateReport: async (reportType, period, hospitalId = null) => {
    const data = { report_type: reportType, period };
    if (hospitalId) data.hospital_id = hospitalId;
    return authClient.post('/admin/reports/generate', data, { timeout: 120000 });
  },

  // Get all scheduled reports
  getSchedules: async () => {
    return authClient.get('/admin/reports/schedules');
  },

  // Create a new scheduled report
  createSchedule: async (scheduleData) => {
    return authClient.post('/admin/reports/schedules', scheduleData);
  },

  // Update an existing scheduled report
  updateSchedule: async (scheduleId, scheduleData) => {
    return authClient.put(`/admin/reports/schedules/${scheduleId}`, scheduleData);
  },

  // Delete a scheduled report
  deleteSchedule: async (scheduleId) => {
    return authClient.delete(`/admin/reports/schedules/${scheduleId}`);
  },

  // Manually trigger sending a scheduled report
  sendReportNow: async (scheduleId) => {
    return authClient.post(`/admin/reports/send/${scheduleId}`);
  },
};

// ===========================================
// Utility Functions
// ===========================================
export const formatters = {
  duration: (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  },

  percentage: (value, decimals = 1) => {
    return `${parseFloat(value).toFixed(decimals)}%`;
  },

  number: (value) => {
    return new Intl.NumberFormat().format(value);
  },

  date: (dateString) => {
    return new Date(dateString).toLocaleDateString();
  },

  datetime: (dateString) => {
    return new Date(dateString).toLocaleString();
  },

  currency: (value, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  },
};

export default apiClient;
